import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar } from "../../atoms/Avatar";
import "./index.scss";

const MAX_VISIBLE_NAMES = 2;

function ChatTypingIndicator({ typingUserIds, resolveUserName }) {
    const activeUserIds = useMemo(() => {
        if (!Array.isArray(typingUserIds)) {
            return [];
        }
        return typingUserIds.filter((id, index, array) => id && array.indexOf(id) === index);
    }, [typingUserIds]);

    if (activeUserIds.length === 0) {
        return null;
    }

    const profiles = activeUserIds.map((userId) => {
        const resolved = resolveUserName(userId);
        if (!resolved) {
            return ({ name: "Someone", avatarUrl: null });
        }
        if (typeof resolved === "string") {
            return ({ name: resolved, avatarUrl: null });
        }
        const { name, username, fullName, avatarUrl } = resolved;
        return ({
            name: name || fullName || username || "Someone",
            avatarUrl: avatarUrl || null,
        });
    });

    const displayParts = profiles.slice(0, MAX_VISIBLE_NAMES).map((profile) => profile.name);
    const primaryProfile = profiles[0] || { name: "Someone", avatarUrl: null };

    const remaining = activeUserIds.length - displayParts.length;

    let message;
    if (remaining > 0) {
        message = `${displayParts.join(", ")} and ${remaining} more are typing…`;
    } else if (displayParts.length === 1) {
        message = `${displayParts[0]} is typing…`;
    } else {
        message = `${displayParts.join(" and ")} are typing…`;
    }

    return (
        <div className="chat-typing-indicator" role="status" aria-live="polite">
            <div className="chat-typing-indicator__bubble chat-bubble chat-bubble--friend">
                {primaryProfile.avatarUrl && (
                    <Avatar
                        className="chat-typing-indicator__avatar"
                        src={primaryProfile.avatarUrl}
                        alt={primaryProfile.name ? `${primaryProfile.name}'s avatar` : "User avatar"}
                        radius="50%"
                    />
                )}
                <span className="typing-dot typing-dot--one" />
                <span className="typing-dot typing-dot--two" />
                <span className="typing-dot typing-dot--three" />
            </div>
            {message && (
                <span className="chat-typing-indicator__label">{message}</span>
            )}
        </div>
    );
}

ChatTypingIndicator.propTypes = {
    typingUserIds: PropTypes.arrayOf(PropTypes.string),
    resolveUserName: PropTypes.func,
};

ChatTypingIndicator.defaultProps = {
    typingUserIds: [],
    resolveUserName: () => "Someone",
};

export default ChatTypingIndicator;
